/**
 * Station views (M2 plan §6 M2-20): one model instance per station, built once the station is `ready` (the
 * stations runtime attached its deferred model) from `instantiate` + `applyStaticLook` in the station's livery
 * (`stations.json look`) + `enableShadows`, turned by the station's rotation and placed in render space every
 * frame (D-011). Building allocates (once per station); `frameStations` is allocation-free (D-014).
 */
import type { Object3D } from 'three/webgpu'
import type { LoadedModel, ModelInstance } from '../../assets/loader'
import type { Vec3 } from '../../core/math'
import { enableShadows } from '../../render/environment'
import { toRenderSpace } from '../../render/floatingOrigin'
import { applyStaticLook, type MaterialLibrary } from '../../render/materials'
import type { StationRuntime } from '../../space/contracts'

export interface StationViews {
  /** Per station index: its instance, or null until built. */
  readonly instances: (ModelInstance | null)[]
  /** Stations not built yet (0 = all built). */
  pending: number
}

export function createStationViews(count: number): StationViews {
  return { instances: new Array<ModelInstance | null>(count).fill(null), pending: count }
}

/**
 * Builds the view of station `s` when it is ready and its model is loaded. Returns true when built (allocates;
 * runs once per station).
 */
export function buildStationView(
  views: StationViews,
  s: Readonly<StationRuntime>,
  models: ReadonlyMap<string, LoadedModel>,
  lib: MaterialLibrary,
  parent: Object3D,
  lodScale: number,
): boolean {
  if (views.instances[s.index] || !s.ready) return false
  const model = models.get(s.def.assetId)
  if (!model) return false
  const instance = model.instantiate(lodScale)
  applyStaticLook(lib, instance, s.def.look)
  enableShadows(instance.root)
  instance.root.name = `station:${s.def.id}`
  instance.root.quaternion.set(s.rot.x, s.rot.y, s.rot.z, s.rot.w)
  parent.add(instance.root)
  views.instances[s.index] = instance
  views.pending--
  return true
}

/** Places every built station in render space. Allocation-free. */
export function frameStations(views: StationViews, list: readonly StationRuntime[], cam: Vec3): void {
  const n = list.length < views.instances.length ? list.length : views.instances.length
  for (let i = 0; i < n; i++) {
    const inst = views.instances[i]
    if (!inst) continue
    const s = list[i]
    toRenderSpace(s.pos.x, s.pos.y, s.pos.z, cam, inst.root.position)
    inst.root.quaternion.set(s.rot.x, s.rot.y, s.rot.z, s.rot.w)
  }
}

export function disposeStationViews(views: StationViews): void {
  for (let i = 0; i < views.instances.length; i++) {
    views.instances[i]?.root.removeFromParent()
    views.instances[i] = null
  }
  views.pending = views.instances.length
}
